import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: '',
    source: 'contact_form'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: 'Message sent successfully!',
          description: 'We will get back to you within 24 hours.',
        });
        setFormData({
          name: '',
          email: '',
          mobile: '',
          message: '',
          source: 'contact_form'
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again or call us directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: '+91 79891 09423',
      link: 'tel:+917989109423'
    },
    {
      icon: Mail,
      title: 'Email',
      details: 'info@rtechnos.com',
      link: 'mailto:info@rtechnos.com'
    },
    {
      icon: MapPin,
      title: 'Address',
      details: 'Hyderabad, Telangana, India',
      link: null
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Mon-Sat: 9:00 AM - 6:00 PM',
      link: null
    }
  ];

  const features = [
    'Free Consultation',
    '24/7 Support',
    'Quick Response Time',
    'Expert Solutions'
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us - R-Technos | Get in Touch for IT Solutions</title>
        <meta name="description" content="Contact R-Technos for IT solutions, services, and support. Reach us via phone, email, or visit our office in Hyderabad. Free consultation available." />
        <meta name="keywords" content="contact R-Technos, IT support Hyderabad, IT consultation, reach us, customer support" />
        <link rel="canonical" href="https://rtechnos.com/contact" />
        <meta property="og:title" content="Contact R-Technos - IT Solutions Provider" />
        <meta property="og:description" content="Get in touch with R-Technos for professional IT solutions and support services." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-white pt-20" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Text, sans-serif' }}>
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-white via-gray-50 to-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-blue-600 text-sm font-semibold tracking-wider uppercase mb-3">
              Get In Touch
            </p>
            <h1 className="text-5xl md:text-6xl font-semibold text-gray-900 mb-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
              Let's Build Something
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 text-transparent bg-clip-text">
                Amazing Together
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Have a project in mind? Our team of experts is ready to help you transform your business with cutting-edge IT solutions.
            </p>
          </div>
        </section>

        {/* Features Banner */}
        <section className="py-8 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center justify-center gap-2">
                  <CheckCircle className="text-blue-600" size={20} />
                  <span className="text-gray-700 font-medium text-sm md:text-base">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-10 border border-gray-100">
                <h2 className="text-3xl font-semibold text-gray-900 mb-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
                  Send us a message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-gray-300 focus:border-blue-600 focus:ring-blue-600"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-gray-300 focus:border-blue-600 focus:ring-blue-600"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number *
                    </label>
                    <Input
                      id="mobile"
                      name="mobile"
                      type="tel"
                      required
                      value={formData.mobile}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-gray-300 focus:border-blue-600 focus:ring-blue-600"
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-gray-300 focus:border-blue-600 focus:ring-blue-600"
                      placeholder="Tell us about your project or requirements..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-full text-base font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-semibold text-gray-900 mb-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
                    Contact Information
                  </h2>
                  <p className="text-gray-600 text-lg mb-8">
                    Reach out to us through any of these channels. We are here to help you with all your IT needs.
                  </p>

                  <div className="space-y-4">
                    {contactInfo.map((info, index) => {
                      const Icon = info.icon;
                      const content = (
                        <div className="flex items-start gap-4 p-5 rounded-2xl bg-gray-50 hover:bg-blue-50 transition-colors">
                          <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                            <Icon className="text-white" size={24} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-1">{info.title}</h3>
                            <p className="text-gray-600">{info.details}</p>
                          </div>
                        </div>
                      );

                      return info.link ? (
                        <a key={index} href={info.link} className="block">
                          {content}
                        </a>
                      ) : (
                        <div key={index}>
                          {content}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Why Choose Us */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif' }}>
                    Why Choose R-Technos?
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-700">10+ years of industry experience</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-700">Certified IT professionals</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-700">Customized solutions for every business</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-700">24/7 technical support available</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-700">Proven track record with 500+ clients</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
              Ready to Get Started?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Schedule a free consultation with our experts today and discover how we can help your business grow.
            </p>
            <a href="tel:+917989109423">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-base rounded-full shadow-lg hover:shadow-xl transition-all">
                <Phone className="mr-2" size={20} />
                Call Now: +91 79891 09423
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
